"use client";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { creativeRoles as data } from "@/lib/data/careers";

const accent = "var(--color-cyan)";

export function CreativeRoles() {
  return (
    <Section id="creative-roles" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Role</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Focus Area</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={row.role} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-baseline gap-2">
                          <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                          <span className="font-semibold">{row.role}</span>
                        </div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed md:hidden">{row.focus}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))] hidden md:table-cell">{row.focus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <Reveal delay={150}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>What You Will Work On</span>
              <ul className="mt-4 space-y-2">
                {data.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                    <span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: accent }} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>What We Look For</span>
              <ul className="mt-4 space-y-2">
                {data.requirements.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                    <span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: accent }} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={300}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
